import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO, { SITE_URL } from '../components/SEO';
import { Blog7 } from '../components/ui/blog7';
import blogPosts from '../data/blogPosts';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.id === slug);

  if (!post) {
    return (
      <div className="bg-white min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center py-24">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Post Not Found</h1>
            <p className="text-gray-500 mb-8">The blog post you're looking for doesn't exist.</p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-primary font-medium hover:text-primary-dark"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Related posts — same label, exclude current, max 3
  const related = blogPosts
    .filter((p) => p.id !== post.id && p.label === post.label)
    .slice(0, 3);

  // If not enough same-label posts, fill with others
  const fillRelated = related.length < 3
    ? blogPosts.filter((p) => p.id !== post.id && !related.find((r) => r.id === p.id)).slice(0, 3 - related.length)
    : [];
  const allRelated = [...related, ...fillRelated];

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Header />
      <SEO
        title={post.title}
        description={post.summary}
        image={post.image}
        url={`/blog/${post.id}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.summary,
          "image": `${SITE_URL}${post.image}`,
          "url": `${SITE_URL}/blog/${post.id}`,
          "datePublished": new Date(post.published.replace(/(\d{2}) (\w{3}) (\d{4})/, '$2 $1, $3')).toISOString(),
          "dateModified": new Date(post.published.replace(/(\d{2}) (\w{3}) (\d{4})/, '$2 $1, $3')).toISOString(),
          "author": {
            "@type": "Organization",
            "name": "Engaged Manila",
            "url": SITE_URL,
          },
          "publisher": {
            "@type": "Organization",
            "name": "Engaged Manila",
            "logo": {
              "@type": "ImageObject",
              "url": `${SITE_URL}/logo512.png`,
            },
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${SITE_URL}/blog/${post.id}`,
          },
        }}
      />

      {/* Hero image */}
      <div className="relative pt-28 pb-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary mb-4">
              {post.label}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
              {post.title}
            </h1>
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
              {post.author && <span>{post.author}</span>}
              {post.author && post.published && <span className="text-gray-300">•</span>}
              {post.published && <span>{post.published}</span>}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Featured image */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <motion.div
          className="relative aspect-video overflow-hidden rounded-2xl shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </motion.div>
      </div>

      {/* Article body */}
      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.content.map((block, index) => {
            if (block.type === 'heading') {
              return (
                <h2
                  key={index}
                  className="text-2xl font-bold tracking-tight text-gray-900 mt-10 mb-4"
                >
                  {block.text}
                </h2>
              );
            }
            return (
              <p
                key={index}
                className="text-lg leading-relaxed text-gray-600 mb-4"
              >
                {block.text}
              </p>
            );
          })}
        </div>
      </article>

      {/* Related posts */}
      {allRelated.length > 0 && (
        <Blog7
          tagline="Keep Reading"
          heading="Related Articles"
          posts={allRelated.map((p) => ({
            id: p.id,
            title: p.title,
            summary: p.summary,
            label: p.label,
            author: p.author,
            published: p.published,
            url: `/blog/${p.id}`,
            image: p.image,
          }))}
        />
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;
